import type { BooleanInput, NumberInput } from "@angular/cdk/coercion";
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  NgZone,
  afterNextRender,
  booleanAttribute,
  computed,
  inject,
  input,
  numberAttribute,
} from "@angular/core";
import { NgIcon, provideIcons } from "@ng-icons/core";
import {
  solarCheckCircleBroken,
  solarInfoCircleBroken,
  solarRestartBroken,
  solarDangerCircleBroken,
  solarDangerTriangleBroken,
} from "@spartan-ng/helm/icons";
import { BrnSonnerImports, type ToasterProps } from "@spartan-ng/brain/sonner";
import { HlmButtonImports } from "@spartan-ng/helm/button";
import { hlm } from "@spartan-ng/helm/utils";
import type { ClassValue } from "clsx";

@Component({
  selector: "hlm-toaster",
  imports: [BrnSonnerImports, HlmButtonImports, NgIcon],
  providers: [
    provideIcons({
      solarCheckCircleBroken,
      solarInfoCircleBroken,
      solarDangerTriangleBroken,
      solarDangerCircleBroken,
      solarRestartBroken,
    }),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <brn-sonner-toaster
      [class]="_computedClass()"
      [invert]="invert()"
      [theme]="theme()"
      [position]="position()"
      [hotKey]="hotKey()"
      [richColors]="richColors()"
      [expand]="expand()"
      [duration]="duration()"
      [visibleToasts]="visibleToasts()"
      [closeButton]="closeButton()"
      [toastOptions]="_computedToastOptions()"
      [offset]="offset()"
      [style]="userStyle()"
    >
      <ng-template #loadingIcon>
        <ng-icon
          name="solarRestartBroken"
          class="overflow-visible! text-base [&>svg]:motion-safe:animate-spin"
        />
      </ng-template>
      <ng-template #successIcon>
        <ng-icon name="solarCheckCircleBroken" class="overflow-visible! text-base" />
      </ng-template>
      <ng-template #errorIcon>
        <ng-icon name="solarDangerCircleBroken" class="overflow-visible! text-base" />
      </ng-template>
      <ng-template #infoIcon>
        <ng-icon name="solarInfoCircleBroken" class="overflow-visible! text-base" />
      </ng-template>
      <ng-template #warningIcon>
        <ng-icon
          name="solarDangerTriangleBroken"
          class="overflow-visible! text-base"
        />
      </ng-template>
      <button
        *brnToastAction="let action; let toast = toast"
        [style]="toast.actionButtonStyle"
        hlmBtn
        size="sm"
        (click)="action.onClick($event)"
      >
        {{ action.label }}
      </button>
      <button
        *brnToastCancelAction="let cancel; let toast = toast"
        [style]="toast.cancelButtonStyle"
        hlmBtn
        size="sm"
        variant="secondary"
        (click)="cancel.onClick($event)"
      >
        {{ cancel.label }}
      </button>
    </brn-sonner-toaster>
  `,
})
export class HlmToaster {
  public readonly invert = input<ToasterProps["invert"], BooleanInput>(false, {
    transform: booleanAttribute,
  });
  public readonly theme = input<ToasterProps["theme"]>("light");
  public readonly position = input<ToasterProps["position"]>("bottom-right");
  public readonly hotKey = input<ToasterProps["hotkey"]>(["altKey", "KeyT"]);
  public readonly richColors = input<ToasterProps["richColors"], BooleanInput>(
    false,
    {
      transform: booleanAttribute,
    },
  );
  public readonly expand = input<ToasterProps["expand"], BooleanInput>(false, {
    transform: booleanAttribute,
  });
  public readonly duration = input<ToasterProps["duration"], NumberInput>(
    4000,
    {
      transform: numberAttribute,
    },
  );
  public readonly visibleToasts = input<
    ToasterProps["visibleToasts"],
    NumberInput
  >(3, {
    transform: numberAttribute,
  });
  public readonly closeButton = input<
    ToasterProps["closeButton"],
    BooleanInput
  >(false, {
    transform: booleanAttribute,
  });
  public readonly toastOptions = input<ToasterProps["toastOptions"]>({});
  /** What the close button of a toast is called for assistive technology (brain's template says "Close toast"). */
  public readonly closeButtonLabel = input<string>("Chiudi notifica");

  protected readonly _computedToastOptions = computed(() => {
    const options = this.toastOptions();
    return {
      ...options,
      classes: {
        ...options?.classes,
        toast: hlm("rounded-2xl!", options?.classes?.toast),
      },
    };
  });
  public readonly offset = input<ToasterProps["offset"]>(null);
  public readonly userClass = input<ClassValue>("", { alias: "class" });
  public readonly userStyle = input<Record<string, string>>(
    {
      "--normal-bg": "var(--popover)",
      "--normal-text": "var(--popover-foreground)",
      "--normal-border": "var(--border)",
      "--border-radius": "var(--radius)",
    },
    { alias: "style" },
  );

  protected readonly _computedClass = computed(() =>
    hlm("toaster group", this.userClass()),
  );

  constructor() {
    // Brain's toast template writes the English label of the close button as a literal and offers no option for it: the
    // buttons are renamed as they appear, outside the zone (reading and writing an attribute needs no change detection).
    const host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
    const ngZone = inject(NgZone);
    const destroyRef = inject(DestroyRef);
    afterNextRender(() =>
      ngZone.runOutsideAngular(() => {
        const rename = () => {
          const label = this.closeButtonLabel();
          host.querySelectorAll("[data-close-button]").forEach((button) => {
            if (button.getAttribute("aria-label") !== label) {
              button.setAttribute("aria-label", label);
            }
          });
        };
        const observer = new MutationObserver(rename);
        observer.observe(host, { childList: true, subtree: true });
        destroyRef.onDestroy(() => observer.disconnect());
        rename();
      }),
    );
  }
}
